import Swal from "sweetalert2";
import { safeJsonParse } from "../factory/helper";


const myOrdenationVariation = [
    "PP",
    "XS",
    "P",
    "S",
    "M",
    "G",
    "L",
    "2G",
    "GG",
    "LL",
    "XL",
    "3G",
    "3L",
    "XXL",
    "XGG",
    "1X",
    "2X",
    "3X",
    "3L",
    "4X",
    "4L",
    "0-3 meses",
    "3-6 meses",
    "6-9 meses",
    "9-12 meses",
    "PP/P",
    "P/M",
    "M/G",
    "G/GG",
    "GG/G1",
    "G1/G2",
    "G2/G3"
];

const getPriority = (item: string) => {
    const index = myOrdenationVariation.indexOf(item);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const sortVariationValues = (values: string[]) => {
    return [...values].sort((a, b) => {
        const priorityA = getPriority(a);
        const priorityB = getPriority(b);

        if (
            priorityA !== Number.MAX_SAFE_INTEGER &&
            priorityB !== Number.MAX_SAFE_INTEGER
        ) {
            return priorityA - priorityB;
        }

        if (priorityA !== Number.MAX_SAFE_INTEGER) return -1;
        if (priorityB !== Number.MAX_SAFE_INTEGER) return 1;

        return a.localeCompare(b, undefined, { numeric: true });
    });
};

type VariationButtonsProps = {
    values: string[];
    enabledValues: string[];
    selectedValue: string;
    onSelect: (value: string) => void;
    variant: "rectangle" | "circle";
};

function VariationButtons({ values, enabledValues, selectedValue, onSelect, variant }: VariationButtonsProps) {
    const sortedValues = sortVariationValues(values);

    return (
        <div className="sizes">
            {sortedValues.map((value, index) => {
                const isEnabled = enabledValues.includes(value);
                const isSelected = value === selectedValue;
                const className =
                    variant === "rectangle"
                        ? isSelected
                            ? "retangle-selected"
                            : "retangle"
                        : isSelected
                            ? "circle-selected"
                            : "circle";
                const classNameWithState = isEnabled
                    ? className
                    : `${className} disabled`;

                return (
                    <button
                        key={`${value}-${index}`}
                        className={classNameWithState}
                        disabled={!isEnabled}
                        onClick={() => isEnabled && onSelect(value)}
                    >
                        {variant === "rectangle" ? (
                            <p>
                                {value}
                                {index + 1 < sortedValues.length}
                            </p>
                        ) : (
                            <p>{value}</p>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

type MultipleVariationTypes = {
    name: string;
    values: string[];
};

type VariationWithAvailability = MultipleVariationTypes & {
    enabledValues: string[];
};

type PropsProductDescription = {
    variations: MultipleVariationTypes[];
    setSelectedVariation: (value: any) => void;
    selectedVariation: any;
    product: any;
    config: any;
    selectVariation: (name: string, value: string) => void;
};

export function ProductVariation({ variations, setSelectedVariation, selectedVariation, product, config, selectVariation }: PropsProductDescription) {
    // Função melhorada validateVariations
    function validateVariations(variationsToValidate: MultipleVariationTypes[]): VariationWithAvailability[] {
        try {
            const hasSelection = Object.keys(selectedVariation).length > 0;

            // Todos os contextos de variação válidos considerando estoque e status
            const baseContexts =
                product?.variations
                    .filter((vrt: any) => (vrt.inventory - vrt.minimumStock > 0 || (config && config.content.pdv.showOutStock)) && vrt.status)
                    .map((vrt: any) => safeJsonParse(vrt.context)) || [];

            // Sem nada selecionado: todas as opções estão habilitadas
            if (!hasSelection) {
                return variationsToValidate.map((variation) => ({
                    ...variation,
                    enabledValues: [...variation.values],
                }));
            }

            // Verifica se a combinação completa escolhida existe
            const fullSelectionContexts = baseContexts.filter((context: any) =>
                Object.entries(selectedVariation).every(
                    ([key, value]) => context[key] === value
                )
            );

            if (fullSelectionContexts.length === 0) {
                // Cria uma mensagem mais específica mostrando as variações selecionadas
                const selectedVariationsText = Object.entries(selectedVariation)
                    .map(([key, value]) => {
                        // Formata o nome da variação para ficar mais legível
                        const formattedKey = key === "NOME_COR" ? "cor" : key.toLowerCase();
                        return `${formattedKey} ${value}`;
                    })
                    .join(" e ");

                const message = selectedVariationsText
                    ? `A combinação de ${selectedVariationsText} não está disponível`
                    : "Combinação inválida";

                Swal.fire({
                    icon: "warning",
                    title: "Variação indisponível",
                    text: message,
                    confirmButtonText: "OK",
                });
                setSelectedVariation({});
                return variationsToValidate.map((variation) => ({
                    ...variation,
                    enabledValues: [...variation.values],
                }));
            }

            // Para cada grade (tamanho, cor, etc) calcula quais valores estão habilitados
            const variationsWithAvailability: VariationWithAvailability[] =
                variationsToValidate.map((variation) => {
                    const contextsForVariation = baseContexts.filter(
                        (context: any) =>
                            Object.entries(selectedVariation)
                                // Ignora a própria variação na filtragem, para que ela dependa das outras
                                .filter(([key]) => key !== variation.name)
                                .every(([key, value]) => context[key] === value)
                    );

                    const enabledSet = new Set<string>();

                    contextsForVariation.forEach((context: any) => {
                        const value = context[variation.name];
                        if (typeof value === "string") {
                            enabledSet.add(value);
                        }
                    });

                    // Se não encontrou nenhum contexto específico, mantém todas habilitadas
                    const enabledValues =
                        enabledSet.size > 0
                            ? variation.values.filter((val) => enabledSet.has(val))
                            : [...variation.values];

                    return {
                        ...variation,
                        enabledValues,
                    };
                });

            return variationsWithAvailability;
        } catch (error) {
            console.error("Erro ao validar variações:", error);
            return variationsToValidate.map((variation) => ({
                ...variation,
                enabledValues: [...variation.values],
            }));
        }
    }

    return (<>
        {validateVariations(variations)
            .filter((variation, idx, allVariations) => {
                // Lógica de omissão de grades baseada em OmitGrid
                if (!config?.content?.pdv?.omitGrid) return true;

                const variationOne = allVariations.every(
                    (v) => v.values.length === 1
                );
                const variationMoreTwo = allVariations.some(
                    (v) => v.values.length >= 3
                );
                return !variationOne || variationMoreTwo;
            })
            .map((variation, idx) => {
                if (
                    variation.name !== "VARIATION" &&
                    variation.values[0] !== "NONE"
                ) {
                    return (
                        <div
                            className={`product-sizes${idx === 0 ? " first" : ""}`}
                            key={variation.name}
                        >
                            <p>
                                {variation.name === "NOME_COR"
                                    ? "COR"
                                    : variation.name}
                                : <span>{selectedVariation[variation.name]}</span>
                            </p>
                            {variation.values[0] && variation.values[0].length > 2 ? (
                                <VariationButtons
                                    values={variation.values}
                                    enabledValues={variation.enabledValues}
                                    selectedValue={selectedVariation[variation.name]}
                                    onSelect={(value) =>
                                        selectVariation(variation.name, value)
                                    }
                                    variant="rectangle"
                                />
                            ) : (
                                <VariationButtons
                                    values={variation.values}
                                    enabledValues={variation.enabledValues}
                                    selectedValue={selectedVariation[variation.name]}
                                    onSelect={(value) =>
                                        selectVariation(variation.name, value)
                                    }
                                    variant="circle"
                                />
                            )}
                        </div>
                    );
                }
            })}
    </>);
}